import { Helmet } from '@dr.pogodin/react-helmet';
import { productPhotoMetadata } from '@iheartjane/menu';
import {
  skipJaneDescription,
  skipJaneTitle,
  useRuntimeConfig,
} from '@iheartjane/runtime-config';
import { statePostalCode } from '@iheartjane/utils';
import type { DeepReadonly } from 'ts-essentials';

/* eslint-disable-next-line no-restricted-imports */
import type { MenuProduct, Product, Store } from '@jane/shared/models';
import { useEcommApp } from '@jane/shared/providers';
import { appendDispensaryToStoreName } from '@jane/shared/util';

import { getCanonicalPath } from './getCanonicalPath';
import { OpenGraph } from './openGraph';

export interface ProductDetailMetaDataProps {
  path: string;
  product: Product | MenuProduct;
  store?: DeepReadonly<Store>;
}

export const ProductDetailMetaData = ({
  path,
  product,
  store,
}: ProductDetailMetaDataProps) => {
  const { isIframe } = useEcommApp();
  const { brand_subtype, kind, brand, name: productName } = product;
  const { city, state, name: storeName } = store || {};
  const runtimeConfig = useRuntimeConfig();
  const {
    isBoostForAll,
    isFramelessEmbedMode,
    isPartnerHosted,
    customProductDetailTitleSuffix,
  } = runtimeConfig;

  const storeLocation =
    city && state ? `${city}, ${statePostalCode(state)}` : undefined;

  const titlePrefix = `Shop ${productName}`;
  const titleSuffix = customProductDetailTitleSuffix
    ? customProductDetailTitleSuffix
    : storeName && city && state
      ? `${appendDispensaryToStoreName(storeName, ' Dispensary')} ${storeLocation}`
      : 'Jane';

  const title = `${titlePrefix} | ${titleSuffix}`;

  const descriptionPrefix = `Find information about the ${productName}${
    brand_subtype ? ' ' + brand_subtype : ''
  }${kind === 'flower' ? ' strain' : ''}${brand ? ' from ' + brand : ''}`;

  const descriptionSuffix =
    storeName && city && state
      ? `for sale at ${appendDispensaryToStoreName(storeName, ' dispensary')} in ${storeLocation}`
      : 'for sale on Jane';

  const description = `${descriptionPrefix} ${descriptionSuffix}`;

  const url = getCanonicalPath({
    inIframe: isIframe,
    isBoostWp: isFramelessEmbedMode && !isBoostForAll,
    pagePath: path,
  });

  const image = productPhotoMetadata(product).url;

  const skipTitle = skipJaneTitle(runtimeConfig);
  const skipDescription = skipJaneDescription(runtimeConfig);

  // We remove preset WP canonicals that don't have the /products/ path because they are incorrect
  // Specific to frameless product pages
  if (isFramelessEmbedMode && !isBoostForAll) {
    const presetCanonical = document.querySelectorAll(
      'link:not([href*="/products/"])[rel="canonical"]'
    )[0];
    if (presetCanonical) {
      presetCanonical.remove();
    }
  }

  return (
    <>
      <Helmet>
        {!skipTitle && <title>{title}</title>}

        {/** Url & product description metadata is set in boost4all controller https://github.com/janetechinc/iheartjane/blob/b36d47cefe38578e391ecdd3d33addf12bd1f425/api/app/lib/boost/compilation/compile.rb#L35 */}
        {!skipDescription && !isBoostForAll && (
          <meta name="description" content={description} />
        )}
        {!isBoostForAll && <link rel="canonical" href={url} />}
      </Helmet>
      <OpenGraph
        description={description}
        title={title}
        url={url}
        image={image}
        type="product"
        // Frameless embeds render on the partner's own domain, so suppress Jane
        // branding the same way isPartnerHosted does.
        isPartnerHosted={isPartnerHosted || isFramelessEmbedMode}
        isBoostForAll={isBoostForAll}
        skipDescription={skipDescription}
        skipTitle={skipTitle}
      />
    </>
  );
};
